/**
 * ChainDiffPanel -- inline "Compare states..." affordance on entity
 * detail pages (P2.8.d).
 *
 * Collapsed by default; clicking "Compare states..." reveals the
 * chain selector + From/To inputs + Compare button. After Apply,
 * the panel stays open so the user can swap chain or dates without
 * re-toggling.
 *
 * Defaults: From = 30 days ago; To = now. Gives the customer a
 * working comparison one click away.
 *
 * Per-entity-kind chain availability:
 *   - IBS / Service / Capability: all 4 chains
 *   - Vendor: blast-radius only
 *   - ConfigurationItem: no chains -- panel renders empty-state
 *     copy (CI findings route through linked_service for chain
 *     state -- the operator clicks through to the service detail
 *     page to diff).
 */
import { useMemo, useState } from "react";
import { Arrow } from "shared/components/Arrow";
import { useTranslation } from "react-i18next";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import { useChainDiff } from "twin/api/hooks";
import {
  dateToNaiveLocalInput,
  formatIsoForDisplay,
  naiveLocalToIsoOffset,
} from "shared/time/timeInputs";
import type {
  ChainDiff,
  ChainName,
  EntityKind,
  ListChange,
  Severity,
} from "shared/api/types";

const CHAIN_AVAILABILITY: Record<EntityKind, readonly ChainName[]> = {
  ibs_service: ["resilience-state", "blast-radius", "process", "obligation"],
  service: ["resilience-state", "blast-radius", "process", "obligation"],
  capability: ["resilience-state", "blast-radius", "process", "obligation"],
  vendor: ["blast-radius"],
  configuration_item: [],
};

const SEVERITY_PILL_CLASSES: Record<Severity, string> = {
  critical: "bg-red-100 text-red-900",
  high: "bg-orange-100 text-orange-900",
  medium: "bg-amber-100 text-amber-900",
  low: "bg-slate-100 text-slate-800",
};

export interface ChainDiffPanelProps {
  entityKind: EntityKind;
  entityId: string;
}

function defaultFromIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return dateToNaiveLocalInput(d);
}

function defaultToIso(): string {
  return dateToNaiveLocalInput(new Date());
}

function DiffBody({ diff }: { diff: ChainDiff }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 mt-3">
      {diff.derivation_version_changed && (
        <p
          data-testid="chain-diff-derivation-pill"
          className="inline-block rounded-full bg-amber-100 text-amber-900 border border-amber-200 px-3 py-1 text-xs"
        >
          {t("modules.twin.chainDiff.derivationBumped", {
            before: diff.derivation_version_before,
            after: diff.derivation_version_after,
          })}
        </p>
      )}

      {diff.is_zero_delta && (
        <p
          data-testid="chain-diff-zero-delta"
          className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3"
        >
          {t("modules.twin.chainDiff.zeroDelta")}
        </p>
      )}

      {diff.scalar_changes.length > 0 && (
        <section data-testid="chain-diff-scalar-section">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {t("modules.twin.chainDiff.scalarSection")}
          </h4>
          <ul className="space-y-1 text-sm">
            {diff.scalar_changes.map((c) => (
              <li
                key={c.field}
                data-testid="chain-diff-scalar-row"
                className="flex gap-3"
              >
                <span className="font-medium text-slate-900">{c.field}</span>
                <span className="text-slate-500 line-through">{String(c.before)}</span>
                <span className="text-slate-500">
                  <Arrow />
                </span>
                <span className="text-emerald-700 font-semibold">
                  {String(c.after)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {diff.count_deltas.length > 0 && (
        <section data-testid="chain-diff-count-section">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {t("modules.twin.chainDiff.countSection")}
          </h4>
          <ul className="space-y-1 text-sm tabular-nums">
            {diff.count_deltas.map((d) => (
              <li
                key={d.field}
                data-testid="chain-diff-count-row"
                className="flex gap-3"
              >
                <span className="font-medium text-slate-900">{d.field}</span>
                <span className="text-slate-500">{d.before}</span>
                <span className="text-slate-500">
                  <Arrow />
                </span>
                <span
                  className={
                    d.after === d.before
                      ? "text-slate-500"
                      : d.after > d.before
                        ? "text-red-700 font-semibold"
                        : "text-emerald-700 font-semibold"
                  }
                >
                  {d.after}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {diff.list_changes.map((lc) => (
        <ListChangeBlock key={lc.field} change={lc} />
      ))}
    </div>
  );
}

function ListChangeBlock({ change }: { change: ListChange }) {
  const { t } = useTranslation();
  if (change.added.length === 0 && change.removed.length === 0) {
    return null;
  }
  return (
    <section data-testid="chain-diff-list-section">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
        {change.field}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">
            {t("modules.twin.chainDiff.listSectionAdded")}
          </p>
          {change.added.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              {t("modules.twin.chainDiff.listEmpty")}
            </p>
          ) : (
            <ul className="space-y-1 text-xs">
              {change.added.map((entry, i) => (
                <li
                  key={i}
                  data-testid="chain-diff-list-added-row"
                  className="bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                >
                  {Object.entries(entry)
                    .map(([k, v]) => `${k}=${String(v)}`)
                    .join(" / ")}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-red-700 mb-1">
            {t("modules.twin.chainDiff.listSectionRemoved")}
          </p>
          {change.removed.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              {t("modules.twin.chainDiff.listEmpty")}
            </p>
          ) : (
            <ul className="space-y-1 text-xs">
              {change.removed.map((entry, i) => (
                <li
                  key={i}
                  data-testid="chain-diff-list-removed-row"
                  className="bg-red-50 border border-red-200 rounded px-2 py-1"
                >
                  {Object.entries(entry)
                    .map(([k, v]) => `${k}=${String(v)}`)
                    .join(" / ")}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

export function ChainDiffPanel({ entityKind, entityId }: ChainDiffPanelProps) {
  const { t, i18n } = useTranslation();
  const dateInputLang = useDateInputLang();
  const availableChains = CHAIN_AVAILABILITY[entityKind] ?? [];

  const [open, setOpen] = useState(false);
  const [selectedChain, setSelectedChain] = useState<ChainName | null>(
    availableChains[0] ?? null,
  );
  const [fromDraft, setFromDraft] = useState<string>(defaultFromIso());
  const [toDraft, setToDraft] = useState<string>(defaultToIso());
  // Committed values: only used by the hook when Apply is pressed.
  const [committedFrom, setCommittedFrom] = useState<string | null>(null);
  const [committedTo, setCommittedTo] = useState<string | null>(null);

  // Memoise the hook options so React Query keeps a stable key
  // across re-renders that don't actually move the inputs.
  const diffOpts = useMemo(
    () => ({
      chain: selectedChain as ChainName,
      entity_kind: entityKind,
      entity_id: entityId,
      from: committedFrom,
      to: committedTo,
    }),
    [selectedChain, entityKind, entityId, committedFrom, committedTo],
  );

  const diff = useChainDiff(diffOpts);

  const onApply = () => {
    const fromIso = naiveLocalToIsoOffset(fromDraft);
    const toIso = naiveLocalToIsoOffset(toDraft);
    if (fromIso && toIso) {
      setCommittedFrom(fromIso);
      setCommittedTo(toIso);
    }
  };

  if (availableChains.length === 0) {
    return (
      <section
        data-testid="chain-diff-unavailable"
        className="text-xs text-slate-500 italic"
      >
        {t("modules.twin.chainDiff.unavailable")}
      </section>
    );
  }

  return (
    <section
      data-testid="chain-diff-panel"
      data-open={open ? "true" : "false"}
      className="border-t border-slate-200 pt-4"
    >
      <button
        type="button"
        data-testid="chain-diff-toggle"
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-medium text-brand-brassDark hover:underline"
      >
        {open
          ? t("modules.twin.chainDiff.toggleOpen")
          : t("modules.twin.chainDiff.toggleClosed")}
      </button>

      {open && (
        /* Round 19 H56 -- round 17 H33's fix did not hold: at phone width
           a date-time input still pushed the owner's page 9 px sideways.
           Each control takes the full row on a phone, and anything a
           browser will not shrink scrolls inside this panel, never the
           page. */
        <div
          data-testid="chain-diff-body"
          className="mt-3 max-w-full space-y-3 overflow-x-auto"
        >
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex min-w-0 max-w-full basis-full flex-col text-xs sm:basis-auto">
              <span className="text-slate-500 mb-1">
                {t("modules.twin.chainDiff.chainLabel")}
              </span>
              <select
                data-testid="chain-diff-chain-select"
                value={selectedChain ?? ""}
                onChange={(e) => setSelectedChain(e.target.value as ChainName)}
                className="rounded border border-slate-300 bg-white px-2 py-1"
              >
                {availableChains.map((c) => (
                  <option key={c} value={c}>
                    {t(`modules.twin.chainDiff.chain.${c}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 max-w-full basis-full flex-col text-xs sm:basis-auto">
              <span className="text-slate-500 mb-1">
                {t("modules.twin.chainDiff.fromLabel")}
              </span>
              <input
                dir="auto"
                data-testid="chain-diff-from-input"
                type="datetime-local"
                lang={dateInputLang}
                value={fromDraft}
                onChange={(e) => setFromDraft(e.target.value)}
                /* Round 17 H33 -- a date-time input keeps its own minimum
                   width; at phone width it pushed the page 9 px sideways. */
                className="w-full min-w-0 max-w-full rounded border border-slate-300 bg-white px-2 py-1"
              />
            </label>
            <label className="flex min-w-0 max-w-full basis-full flex-col text-xs sm:basis-auto">
              <span className="text-slate-500 mb-1">
                {t("modules.twin.chainDiff.toLabel")}
              </span>
              <input
                dir="auto"
                data-testid="chain-diff-to-input"
                type="datetime-local"
                lang={dateInputLang}
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                /* Round 17 H33 -- a date-time input keeps its own minimum
                   width; at phone width it pushed the page 9 px sideways. */
                className="w-full min-w-0 max-w-full rounded border border-slate-300 bg-white px-2 py-1"
              />
            </label>
            <button
              type="button"
              data-testid="chain-diff-apply"
              onClick={onApply}
              className="rounded bg-brand-brass text-brand-navy text-xs font-medium px-3 py-1 hover:bg-brand-brassDark"
            >
              {t("modules.twin.chainDiff.applyButton")}
            </button>
          </div>

          {!committedFrom || !committedTo ? (
            <p
              data-testid="chain-diff-empty-prompt"
              className="text-xs text-slate-500 italic"
            >
              {t("modules.twin.chainDiff.emptyPrompt")}
            </p>
          ) : diff.isLoading ? (
            <p data-testid="chain-diff-loading" className="text-sm text-slate-500">
              {t("modules.twin.chainDiff.loading")}
            </p>
          ) : diff.isError ? (
            <p
              data-testid="chain-diff-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
            >
              <strong className="block font-semibold mb-1">
                {t("modules.twin.chainDiff.errorTitle")}
              </strong>
              {diff.error.detail}
            </p>
          ) : diff.data ? (
            <>
              <p className="text-xs text-slate-500">
                {formatIsoForDisplay(diff.data.from, i18n.language)} <Arrow />{" "}
                {formatIsoForDisplay(diff.data.to, i18n.language)}
              </p>
              <DiffBody diff={diff.data} />
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}

// Re-export severity pill class map so tests can assert on it if
// they need to. Currently unused externally but documents the
// design intent (severity classes are stable across DIFF + cascade).
export { SEVERITY_PILL_CLASSES };
